// src/HomePage/Game/FolkGame/Caro/CaroLobby.js
// FIXED: click overlay đóng modal, Esc đóng modal, giới hạn độ dài input
import React, { useState, useEffect, useCallback } from 'react';
import './CaroLobby.css';

const MAX_ROOM_NAME = 60;
const MAX_PASSWORD = 50;
const MAX_PLAYER_NAME = 40;

const CaroLobby = ({
  rooms,
  isLoading,
  currentUser,
  onCreateRoom,
  onJoinRoom,
  onQuickMatch,
  onPlayWithBot,
  onBackToPortal,
  errorMessage,
  onClearError
}) => {
  const [filter, setFilter] = useState('all');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBotModal, setShowBotModal] = useState(false);
  const [roomNameInput, setRoomNameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [targetPassRoom, setTargetPassRoom] = useState(null);
  const [joinPasswordInput, setJoinPasswordInput] = useState('');

  const getPlayerName = (player) => {
    if (!player) return '';
    const holy = player.holyName ? `${player.holyName} ` : '';
    const full = player.fullName || player.name || 'Người chơi';
    return `${holy}${full}`.trim();
  };

  // Đóng modal chung
  const closeAllModals = useCallback(() => {
    setShowCreateModal(false);
    setShowBotModal(false);
    setTargetPassRoom(null);
  }, []);

  // Esc để đóng modal
  useEffect(() => {
    if (!showCreateModal && !showBotModal && !targetPassRoom) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        closeAllModals();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showCreateModal, showBotModal, targetPassRoom, closeAllModals]);

  const handleOpenCreateModal = () => {
    if (!currentUser?.id) return;
    setRoomNameInput(`Bàn của ${getPlayerName(currentUser)}`.slice(0, MAX_ROOM_NAME));
    setPasswordInput('');
    setShowCreateModal(true);
  };

  const handleSubmitCreate = (e) => {
    e.preventDefault();
    if (!roomNameInput.trim()) return;
    onCreateRoom(roomNameInput.trim().slice(0, MAX_ROOM_NAME), passwordInput.slice(0, MAX_PASSWORD));
    setShowCreateModal(false);
  };

  const handleJoinClick = (room) => {
    if (room.hasPassword) {
      setTargetPassRoom(room);
      setJoinPasswordInput('');
    } else {
      onJoinRoom(room.id, '');
    }
  };

  const handleSubmitPass = (e) => {
    e.preventDefault();
    if (targetPassRoom) {
      onJoinRoom(targetPassRoom.id, joinPasswordInput.slice(0, MAX_PASSWORD));
      setTargetPassRoom(null);
    }
  };

  const handleSelectBotDifficulty = (difficulty) => {
    setShowBotModal(false);
    onPlayWithBot(difficulty);
  };

  // Click overlay để đóng modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeAllModals();
    }
  };

  const filteredRooms = rooms.filter((r) => {
    if (filter === 'waiting') return r.status === 'waiting';
    if (filter === 'playing') return r.status === 'playing';
    return true;
  });

  const waitingCount = rooms.filter((r) => r.status === 'waiting').length;
  const playingCount = rooms.filter((r) => r.status === 'playing').length;

  return (
    <div className="caro-lobby-container">
      {/* Thanh quay về Cổng */}
      <div className="caro-lobby-top-bar">
        <button className="caro-lobby-back-btn" onClick={onBackToPortal}>
          ⬅️ Về Danh Mục Game
        </button>
        <span className="caro-lobby-title-pill">⭕❌ Sảnh Cờ Caro</span>
      </div>

      {/* Toolbar công cụ */}
      <div className="caro-lobby-toolbar">
        <div className="caro-actions-group">
          <button className="caro-btn-action create" onClick={handleOpenCreateModal}>
            ➕ Tạo Bàn Mới
          </button>
          <button
            className="caro-btn-action quick"
            onClick={onQuickMatch}
            disabled={isLoading}
          >
            {isLoading ? '⏳ Đang tìm...' : '⚡ Ghép Nhanh'}
          </button>
          <button className="caro-btn-action bot" onClick={() => setShowBotModal(true)}>
            🤖 Đấu Với Máy
          </button>
        </div>

        <div className="caro-filter-tabs">
          <button
            className={`caro-tab-pill ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Tất cả ({rooms.length})
          </button>
          <button
            className={`caro-tab-pill ${filter === 'waiting' ? 'active' : ''}`}
            onClick={() => setFilter('waiting')}
          >
            Đang chờ ({waitingCount})
          </button>
          <button
            className={`caro-tab-pill ${filter === 'playing' ? 'active' : ''}`}
            onClick={() => setFilter('playing')}
          >
            Đang chơi ({playingCount})
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="caro-error-banner" role="alert">
          ⚠️ {errorMessage}
          <button onClick={onClearError} aria-label="Đóng thông báo">✕</button>
        </div>
      )}

      {/* Danh sách phòng */}
      <div className="caro-room-grid">
        {isLoading ? (
          <div className="caro-loading">Đang tải danh sách bàn cờ... ⏳</div>
        ) : filteredRooms.length === 0 ? (
          <div className="caro-empty-state">
            <span className="caro-empty-icon">🎲</span>
            <p>
              {filter === 'all'
                ? <>Chưa có bàn chơi nào. Hãy bấm <b>Tạo Bàn Mới</b> để rủ bạn bè cùng so tài!</>
                : filter === 'waiting'
                  ? 'Hiện chưa có bàn nào đang chờ. Hãy thử tạo bàn mới nhé!'
                  : 'Hiện không có bàn nào đang thi đấu.'}
            </p>
          </div>
        ) : (
          filteredRooms.map((room) => {
            const isFull = Boolean(room.player2);
            const isMine = room.player1?.id === currentUser?.id || room.player2?.id === currentUser?.id;
            return (
              <div key={room.id} className="caro-room-card">
                <div className="caro-card-top">
                  <div className="caro-card-title">
                    <h4>{room.name}</h4>
                    {room.hasPassword && <span title="Có mật khẩu">🔒</span>}
                  </div>
                  <span className={`caro-status-badge ${room.status}`}>
                    {room.status === 'waiting' ? 'Đang chờ 1/2' : 'Đang đấu 2/2'}
                  </span>
                </div>

                <div className="caro-card-players">
                  <div className="caro-player-tag">
                    <span>{room.player1?.avatar || '👤'}</span>
                    <span className="caro-player-name">{getPlayerName(room.player1)}</span>
                  </div>
                  <span className="caro-vs-mini">vs</span>
                  <div className="caro-player-tag">
                    <span>{room.player2 ? (room.player2.avatar || '👤') : '❓'}</span>
                    <span className="caro-player-name">
                      {room.player2 ? getPlayerName(room.player2) : 'Chờ đối thủ'}
                    </span>
                  </div>
                </div>

                <button
                  className="caro-btn-join"
                  disabled={isFull && !isMine}
                  onClick={() => handleJoinClick(room)}
                >
                  {isMine
                    ? 'Vào Lại Bàn'
                    : isFull
                      ? 'Đã Đầy'
                      : 'Vào Chơi ⚔️'}
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* MODAL 1: CHỌN CẤP ĐỘ BOT AI */}
      {showBotModal && (
        <div className="caro-modal-overlay" onClick={handleOverlayClick}>
          <div className="caro-modal-box" role="dialog" aria-modal="true" aria-labelledby="bot-modal-title">
            <h3 id="bot-modal-title">🤖 Chọn Cấp Độ Luyện Tập</h3>
            <p>Chọn trình độ AI phù hợp với bạn để bắt đầu ván đấu:</p>

            <div className="caro-difficulty-grid">
              <button
                type="button"
                className="caro-diff-btn easy"
                onClick={() => handleSelectBotDifficulty('easy')}
              >
                <span className="caro-diff-icon">🌱</span>
                <div className="caro-diff-meta">
                  <strong>Cấp Độ: Dễ (Tập Sự)</strong>
                  <small>AI cơ bản, phù hợp cho người mới làm quen</small>
                </div>
              </button>

              <button
                type="button"
                className="caro-diff-btn medium"
                onClick={() => handleSelectBotDifficulty('medium')}
              >
                <span className="caro-diff-icon">⚔️</span>
                <div className="caro-diff-meta">
                  <strong>Cấp Độ: Trung Bình (Thử Thách)</strong>
                  <small>Công thủ vững chắc, biết chặn các thế 3 ô mở</small>
                </div>
              </button>

              <button
                type="button"
                className="caro-diff-btn hard"
                onClick={() => handleSelectBotDifficulty('hard')}
              >
                <span className="caro-diff-icon">👑</span>
                <div className="caro-diff-meta">
                  <strong>Cấp Độ: Khó (Cao Thủ)</strong>
                  <small>Áp dụng khai cuộc chuẩn & bẫy nước đôi khó cản</small>
                </div>
              </button>
            </div>

            <div className="caro-modal-actions">
              <button
                type="button"
                className="caro-modal-cancel"
                onClick={() => setShowBotModal(false)}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: TẠO BÀN */}
      {showCreateModal && (
        <div className="caro-modal-overlay" onClick={handleOverlayClick}>
          <div className="caro-modal-box" role="dialog" aria-modal="true" aria-labelledby="create-modal-title">
            <h3 id="create-modal-title">✨ Tạo Bàn Cờ Caro</h3>
            <p>Đặt tên và mật khẩu (nếu muốn chơi riêng tư):</p>
            <form onSubmit={handleSubmitCreate} className="caro-modal-form">
              <div>
                <label htmlFor="caro-room-name">Tên bàn đấu:</label>
                <input
                  id="caro-room-name"
                  type="text"
                  value={roomNameInput}
                  onChange={(e) => setRoomNameInput(e.target.value.slice(0, MAX_ROOM_NAME))}
                  placeholder="Nhập tên bàn..."
                  className="caro-modal-input"
                  maxLength={MAX_ROOM_NAME}
                  required
                />
                <small className="caro-input-hint">{roomNameInput.length}/{MAX_ROOM_NAME}</small>
              </div>

              <div>
                <label htmlFor="caro-room-password">Mật khẩu (để trống nếu công khai):</label>
                <input
                  id="caro-room-password"
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value.slice(0, MAX_PASSWORD))}
                  placeholder="Mật khẩu..."
                  className="caro-modal-input"
                  maxLength={MAX_PASSWORD}
                />
              </div>

              <div className="caro-modal-actions">
                <button
                  type="button"
                  className="caro-modal-cancel"
                  onClick={() => setShowCreateModal(false)}
                >
                  Hủy
                </button>
                <button type="submit" className="caro-modal-submit">
                  Tạo Bàn 🚀
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: NHẬP MẬT KHẨU */}
      {targetPassRoom && (
        <div className="caro-modal-overlay" onClick={handleOverlayClick}>
          <div className="caro-modal-box" role="dialog" aria-modal="true" aria-labelledby="pass-modal-title">
            <h3 id="pass-modal-title">🔒 Bàn Chơi Có Mật Khẩu</h3>
            <p>Nhập mật khẩu để vào: <b>{targetPassRoom.name}</b></p>
            <form onSubmit={handleSubmitPass} className="caro-modal-form">
              <div>
                <input
                  type="password"
                  value={joinPasswordInput}
                  onChange={(e) => setJoinPasswordInput(e.target.value.slice(0, MAX_PASSWORD))}
                  placeholder="Nhập mật khẩu bàn..."
                  className="caro-modal-input"
                  maxLength={MAX_PASSWORD}
                  autoFocus
                  required
                />
              </div>

              <div className="caro-modal-actions">
                <button
                  type="button"
                  className="caro-modal-cancel"
                  onClick={() => setTargetPassRoom(null)}
                >
                  Hủy
                </button>
                <button type="submit" className="caro-modal-submit">
                  Vào Bàn 🚀
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaroLobby;
